// P&L of a trade at a price, shared by the details modal and the community mocks

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
// "1d 2h", "4m 12s": the two biggest non-zero units
const TIME_HELD_UNITS = 2;
const MOCK_OPEN_FEE_SHARE = 0.01;
const CENTS_PER_DOLLAR = 100;

/** Position size in coins: the wager times the leverage at the entry price */
export const getQuantity = (wager, leverage, entryPrice) =>
  wager > 0 && leverage > 0 && entryPrice > 0
    ? (wager * leverage) / entryPrice
    : null;

/**
 * Amounts of a mock trade, the real ones come from the backend: the fee is taken
 * out of the wager on opening. An open trade shows what is left of the wager and
 * a P&L without the fee, a closed one the whole wager and a P&L with the fee.
 *
 * @param {number} wager the whole wager
 * @param {boolean} isOpen
 * @returns {{margin: number, wager: number, feeInPnl: number}}
 */
export const getMockTradeAmounts = (wager, isOpen) => {
  const fee =
    Math.round(wager * MOCK_OPEN_FEE_SHARE * CENTS_PER_DOLLAR) / CENTS_PER_DOLLAR;
  const margin = wager - fee;

  return isOpen
    ? { margin, wager: margin, feeInPnl: 0 }
    : { margin, wager, feeInPnl: fee };
};

/**
 * @param {{isLong: boolean, entryPrice: number, quantity: number, wager: number, feeInPnl?: number}} trade
 *   feeInPnl: the open fee, a closed trade's P&L includes it
 * @param {number|null} price
 * @returns {number|null}
 */
export const getTradePnl = (
  { isLong, entryPrice, quantity, wager, feeInPnl = 0 },
  price,
) => {
  if (!entryPrice || !quantity || price == null) return null;

  const pnl =
    (isLong ? price - entryPrice : entryPrice - price) * quantity - feeInPnl;

  // The trade is liquidated when the whole wager is lost
  return wager > 0 ? Math.max(pnl, -wager) : pnl;
};

/** Payout per dollar wagered, 1.39 for a +39% trade */
export const getMultiplier = (wager, pnl) =>
  wager > 0 && pnl != null ? Math.max(0, (wager + pnl) / wager) : null;

export const formatMultiplier = (multiplier) =>
  multiplier == null ? "—" : `${multiplier.toFixed(2)}x`;

/**
 * @param {number|null} openedAt ms
 * @param {number|null} closedAt ms
 * @returns {string|null}
 */
export const formatTimeHeld = (openedAt, closedAt) => {
  if (openedAt == null || closedAt == null || closedAt < openedAt) return null;

  const seconds = Math.floor((closedAt - openedAt) / MS_PER_SECOND);
  const units = [
    ["d", Math.floor(seconds / SECONDS_PER_DAY)],
    ["h", Math.floor(seconds / SECONDS_PER_HOUR) % HOURS_PER_DAY],
    ["m", Math.floor(seconds / SECONDS_PER_MINUTE) % MINUTES_PER_HOUR],
    ["s", seconds % SECONDS_PER_MINUTE],
  ];
  const first = units.findIndex(([, amount]) => amount > 0);

  if (first === -1) return "0s";

  return units
    .slice(first, first + TIME_HELD_UNITS)
    .filter(([, amount]) => amount > 0)
    .map(([unit, amount]) => `${amount}${unit}`)
    .join(" ");
};
